import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  ADD_QUESTION_AUDIO_FILE_USE_CASE,
  ADD_QUESTION_FILE_USE_CASE,
  AddQuestionFileUseCaseInput,
  CANCEL_QUESTION_USE_CASE,
  CancelQuestionUseCaseOutput,
  CREATE_QUESTION_USE_CASE,
  CreateQuestionUseCaseOutput,
  DELETE_QUESTION_AUDIO_FILE_USE_CASE,
  DELETE_QUESTION_USE_CASE,
  DeleteQuestionUseCaseInput,
  DeleteQuestionUseCaseOutput,
  MOVE_QUESTION_USE_CASE,
  MoveQuestionUseCaseOutput,
  PreviousQuestionId,
  Question,
  QuestionProps,
  Questions,
  QuestionStatus,
  SAVE_QUESTION_USE_CASE,
  SectionSummary,
  UPDATE_QUESTION_AUDIO_FILE_USE_CASE,
  UPDATE_QUESTION_TYPE_USE_CASE,
  UPDATE_QUESTION_USE_CASE,
  UpdateQuestionInput,
  UpdateQuestionUseCaseInput,
} from '@module/form';
import { FormEditPageState } from '../../form-edit.states';
import { withSyncedFormVersion, withSyncedFormVersionFromEntity } from '../../sync-form-version';
import { OPEN_EDIT_QUESTION_USE_CASE, SET_QUESTION_IN_VIEW } from '../../../../redux/root.action';
import {
  AddQuestionFileUseCaseState,
  CreateQuestionUseCaseState,
  CreateQuestionAnswerFileUseCaseState,
  CreateQuestionFileUseCaseState,
  DeleteQuestionUseCaseState,
  MoveQuestionUseCaseState,
  QuestionInViewUseCaseState,
  SaveQuestionUseCaseState,
  UpdateQuestionUseCaseState,
  OpenEditQuestionUseCaseState,
  UpdateDraftQuestionUseCaseState,
  UpdateQuestionAudioFileUseCaseState,
  AddQuestionAudioFileUseCaseState,
  DeleteQuestionAudioFileUseCaseState,
} from './question-item.states';
import { answerItemHandlers, handleCreateAnswerStateUseCase } from './answer-item';
import {
  CREATE_QUESTION_ANSWER_FILE_STORAGE_USE_CASE,
  CREATE_QUESTION_FILE_STORAGE_USE_CASE,
  CreatedFileStorage,
} from '@module/file-storage';
import { cloneDeep } from 'lodash';

// Simple action to clear all draft questions (used when entering AI Mode)
export const CLEAR_DRAFT_QUESTIONS = 'CLEAR_DRAFT_QUESTIONS';

const getFormTotals = (questions: Question<QuestionProps>[] = []) => ({
  formTotalQuestions: Questions.getTotalQuestions(questions),
  formTotalScore: Questions.getTotalScore(questions),
});

export const handleOpenEditQuestion: UseCaseStateGenerator<OpenEditQuestionUseCaseState> = {
  name: OPEN_EDIT_QUESTION_USE_CASE,
  executing: (state: FormEditPageState, action: Action) => {
    const questionId = action.payload;
    const { questions, headings } = state;
    const foundQuestion = questions.find((q) => q.id.toString() === questionId);

    if (!foundQuestion) {
      return state; // Question not found, return state as is
    }

    const revertedQuestion = foundQuestion.clone();
    const backupQuestions = questions.map((q) => q.clone());
    const backupHeadings = cloneDeep([...headings]);

    // Create draft question - clone the question for editing
    const draftQuestion = foundQuestion.clone();

    return {
      ...state,
      editingQuestionId: questionId,
      // Opening edit is not an unsaved change; mark dirty only when the draft is modified.
      hasUnsavedQuestionOrSectionChanges: false,
      selectedFormItemId: questionId,
      editingFormItemId: questionId,
      revertedQuestion,
      backupHeadings,
      backupQuestions,
      draftQuestions: {
        [questionId]: draftQuestion,
      },
    };
  },
  reset: (state: FormEditPageState) => {
    return {
      ...state,
      editingQuestionId: null,
      isEditingQuestion: false,
      revertedQuestion: null,
      editingFormItemId: '',
      // Don't clear draftQuestions here - will be cleared on save/cancel
    };
  },
};

// Reducer to clear all draft questions when requested (e.g. entering AI Mode)
export const handleClearDraftQuestions = {
  name: CLEAR_DRAFT_QUESTIONS,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      draftQuestions: {},
      editingQuestionId: null,
      editingFormItemId: '',
      isQuestionEditing: false,
      hasUnsavedQuestionOrSectionChanges: false,
      revertedQuestion: null,
    };
  },
};

export const handleSaveQuestionUseCase: UseCaseStateGenerator<SaveQuestionUseCaseState> = {
  name: SAVE_QUESTION_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      saveQuestionStatus: RequestStatus.EXECUTE,
      isQuestionEditing: false,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const updatedQuestion = payload as Question<QuestionProps>;
    const questionId = updatedQuestion.id.toString();

    // Replace in place to keep other question object identities stable (less list churn).
    const existingIndex = state.questions.findIndex((q) => q.id.toString() === questionId);
    const newQuestions =
      existingIndex === -1
        ? [...state.questions, updatedQuestion]
        : state.questions.map((q, index) => (index === existingIndex ? updatedQuestion : q));

    // Update sectionSummaries only when question is saved
    const sectionSummaries = [...state.sectionSummaries];
    const updatedSectionSummaryIndex = sectionSummaries.findIndex(
      (section) => section.id.toString() === updatedQuestion.sectionId,
    );
    if (updatedSectionSummaryIndex !== -1) {
      sectionSummaries[updatedSectionSummaryIndex] = SectionSummary.cloneFromSectionAndQuestions(
        sectionSummaries[updatedSectionSummaryIndex],
        newQuestions,
      );
    }

    // NOTE: Remove draft question from draftQuestions after successful save
    // The question has been saved to server and synced to questions state, so draft is no longer needed
    const draftQuestions = { ...(state.draftQuestions || {}) };
    delete draftQuestions[questionId];

    withSyncedFormVersionFromEntity(state, updatedQuestion);

    return {
      ...state,
      hasEssayOrFillInBlankQuestion: Questions.hasEssayOrFillInBlankQuestion(newQuestions),
      saveQuestionStatus: RequestStatus.SUCCESS,
      editingQuestionId: null,
      unsavedQuestion: null,
      hasUnsavedQuestionOrSectionChanges: false,
      isQuestionEditing: false,
      revertedQuestion: null,
      // Smart selection: Only update selectedFormItemId if NOT editing another question
      // If editing another question (editingFormItemId exists and differs from saved question),
      // keep current selection to avoid UI jitter
      selectedFormItemId:
        state.editingFormItemId && state.editingFormItemId !== updatedQuestion.id.toString()
          ? state.selectedFormItemId // Keep current selection when editing another question
          : updatedQuestion.id.toString(), // Select saved question for regular save
      // Keep editingFormItemId for now, will be handled by container useEffect
      editingFormItemId: state.editingFormItemId,
      questions: newQuestions, // Update actual questions state
      isFormHasQuestions:
        (state.sections || []).length > 0 &&
        newQuestions.some((q) => {
          const status = (q as any).status;
          return (
            status && status !== 'New' && status !== 'ChangeNewType' && status !== 'Deactivate'
          );
        }),
      sectionSummaries,
      ...getFormTotals(newQuestions),
      backupQuestions: null,
      backupHeadings: null,
      backupSelectedItem: null,
      draftQuestions, // Remove saved question from drafts
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      saveQuestionStatus: RequestStatus.ERROR,
      hasUnsavedQuestionOrSectionChanges: false,
      isQuestionEditing: false,
      editingFormItemId: '',
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      editingQuestionId: null,
      saveQuestionStatus: RequestStatus.RESET,
      updateQuestionOrAnswerStatus: RequestStatus.RESET,
      hasUnsavedQuestionOrSectionChanges: false,
      isQuestionEditing: false,
      editingFormItemId: '',
    };
  },
};

export const handleCreateLocalQuestionUseCase: UseCaseStateGenerator<CreateQuestionUseCaseState> = {
  name: CREATE_QUESTION_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => {
    const backupQuestions = state.questions.map((q) => q.clone());
    const backupHeadings = cloneDeep([...state.headings]);
    const backupSelectedItem = state.selectedFormItemId;
    return {
      ...state,
      createdQuestionStatus: RequestStatus.EXECUTE,
      isQuestionEditing: true,
      backupHeadings,
      backupQuestions,
      backupSelectedItem,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const sectionSummaries = [...state.sectionSummaries];

    const { payload } = action;
    const output = payload as CreateQuestionUseCaseOutput;
    const { newQuestion, newHeadings, questions } = output;
    const sortedQuestions = Questions.sortQuestionsWithIndexAndSection(questions);
    const updatedSectionIndex = sectionSummaries.findIndex(
      (section) => section.id.toString() === newQuestion.sectionId,
    );

    sectionSummaries[updatedSectionIndex] = SectionSummary.cloneFromSectionAndQuestions(
      sectionSummaries[updatedSectionIndex],
      sortedQuestions,
    );

    // Create draft question for new question (same as handleOpenEditQuestion)
    // This ensures new questions can use draft question for add/delete/update answers
    const questionId = newQuestion.id.toString();
    const draftQuestion = newQuestion.clone();
    const draftQuestions = {
      ...(state.draftQuestions || {}),
      [questionId]: draftQuestion,
    };

    return {
      ...state,
      sectionSummaries,
      questions,
      ...getFormTotals(questions),
      headings: newHeadings,
      isFormHasQuestions:
        (state.sections || []).length > 0 &&
        questions.some((q) => {
          const status = (q as any).status;
          return (
            status && status !== 'New' && status !== 'ChangeNewType' && status !== 'Deactivate'
          );
        }),
      createdQuestionStatus: RequestStatus.SUCCESS,
      hasUnsavedQuestionOrSectionChanges: true,
      newQuestion: newQuestion.clone(),
      editingQuestionId: newQuestion.id.toString(),
      editingFormItemId: newQuestion.id.toString(),
      backupSelectedItem: state.selectedFormItemId,
      selectedFormItemId: newQuestion.id.toString(),
      draftQuestions, // Add draft question for new question
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      createdQuestionStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      createdQuestionStatus: RequestStatus.RESET,
    };
  },
};

export const handleCreateQuestionFileUseCase: UseCaseStateGenerator<CreateQuestionFileUseCaseState> =
  {
    name: CREATE_QUESTION_FILE_STORAGE_USE_CASE,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      return {
        ...state,
        uploadingFile: action.payload.file,
        createQuestionFileStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { payload } = action;

      return {
        ...state,
        createQuestionFileStatus: RequestStatus.SUCCESS,
        selectedFile: payload as CreatedFileStorage,
      };
    },
    error: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { error } = action;
      return {
        ...state,
        error: error,
        createQuestionFileStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        selectedFile: null,
        createQuestionFileStatus: RequestStatus.RESET,
      };
    },
  };
export const handleCreateQuestionAnswerFileUseCase: UseCaseStateGenerator<CreateQuestionAnswerFileUseCaseState> =
  {
    name: CREATE_QUESTION_ANSWER_FILE_STORAGE_USE_CASE,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      return {
        ...state,
        uploadingFile: action.payload.file,
        createQuestionAnswerFileStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { payload } = action;

      return {
        ...state,
        createQuestionAnswerFileStatus: RequestStatus.SUCCESS,
        selectedFile: payload as CreatedFileStorage,
      };
    },
    error: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { error } = action;
      return {
        ...state,
        error: error,
        createQuestionAnswerFileStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        selectedFile: null,
        createQuestionAnswerFileStatus: RequestStatus.RESET,
      };
    },
  };

export const handleAddQuestionFileUseCase: UseCaseStateGenerator<AddQuestionFileUseCaseState> = {
  name: ADD_QUESTION_FILE_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      addQuestionFileInput: action.payload as AddQuestionFileUseCaseInput,
      addQuestionFileStatus: RequestStatus.EXECUTE,
      isQuestionEditing: false,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const updatedQuestion = payload as Question<QuestionProps>;
    const questions: Question<QuestionProps>[] = state.questions.filter(
      (q) => q.id.toString() !== updatedQuestion.id.toString(),
    );
    questions.push(updatedQuestion);
    const sortQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

    return {
      ...state,
      questions: sortQuestions,
      ...getFormTotals(sortQuestions),
      unsavedQuestion: updatedQuestion,
      addQuestionFileStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      addQuestionFileStatus: RequestStatus.ERROR,
      isQuestionEditing: false,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      selectedFile: null,
      addQuestionFileStatus: RequestStatus.RESET,
      isQuestionEditing: false,
    };
  },
};

export const handleUpdateQuestionStateUseCase: UseCaseStateGenerator<UpdateQuestionUseCaseState> = {
  name: UPDATE_QUESTION_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    return {
      ...state,
      updateQuestionUseCaseInput: action.payload as UpdateQuestionUseCaseInput<UpdateQuestionInput>,
      updateQuestionStatus: RequestStatus.EXECUTE,
      updateQuestionOrAnswerStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const updatedQuestion = payload as Question<QuestionProps>;
    const { questions } = state;

    const newQuestions = questions.filter((q) => q.id.toString() !== updatedQuestion.id.toString());
    newQuestions.push(updatedQuestion);

    // Remove sectionSummaries update - only update when save, not real-time
    return {
      ...state,
      questions: newQuestions,
      ...getFormTotals(newQuestions),
      unsavedQuestion: updatedQuestion,
      updateQuestionStatus: RequestStatus.SUCCESS,
      updateQuestionOrAnswerStatus: RequestStatus.SUCCESS,
      updatedQuestion,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    const { revertedQuestion } = state;
    const questions = state.questions.filter(
      (q) => q.id.toString() !== revertedQuestion.id.toString(),
    );
    questions.push(revertedQuestion);

    return {
      ...state,
      revertedQuestion: null,
      hasUnsavedQuestionOrSectionChanges: false,
      editingQuestionId: null,
      updateQuestionStatus: RequestStatus.RESET,
      updateQuestionOrAnswerStatus: RequestStatus.RESET,
    };
  },
};

export const handleUpdateQuestionTypeStateUseCase: UseCaseStateGenerator<UpdateQuestionUseCaseState> =
  {
    ...handleUpdateQuestionStateUseCase,
    name: UPDATE_QUESTION_TYPE_USE_CASE,
    success: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { payload } = action;
      const updatedQuestion = payload as Question<QuestionProps>;
      const { questions } = state;
      const questionId = updatedQuestion.id.toString();

      const newQuestions = questions.filter((q) => q.id.toString() !== questionId);
      newQuestions.push(updatedQuestion);

      // Create draft question - clone the question for editing
      const draftQuestions = {
        ...(state.draftQuestions || {}),
        [questionId]: updatedQuestion.clone(),
      };

      return {
        ...state,
        questions: newQuestions,
        ...getFormTotals(newQuestions),
        unsavedQuestion: updatedQuestion,
        updateQuestionStatus: RequestStatus.SUCCESS,
        updateQuestionOrAnswerStatus: RequestStatus.SUCCESS,
        updatedQuestion,
        draftQuestions,
      };
    },
  };

// Update draft question use case
export const UPDATE_DRAFT_QUESTION_USE_CASE = 'UPDATE_DRAFT_QUESTION_USE_CASE';
export const FORM_EDIT_AI_UPDATE_QUESTION = 'FORM_EDIT_AI_UPDATE_QUESTION';

export const handleAIUpdateQuestion: UseCaseStateGenerator<UpdateDraftQuestionUseCaseState> = {
  name: FORM_EDIT_AI_UPDATE_QUESTION,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { questionId, field, value } = action.payload as {
      questionId: string;
      field: string;
      value: string;
    };

    const targetQuestion = state.questions.find((q) => q.id.toString() === questionId);
    if (!targetQuestion) return state;

    const updatedQuestion = targetQuestion.clone();

    // Field mapping: AI commands might use 'title' or 'content' for description
    if (field === 'description' || field === 'title' || field === 'content') {
      updatedQuestion.updateDescription(value);
    } else if (field === 'explanation') {
      updatedQuestion.updateExplanation(value);
    }

    const draftQuestions = {
      ...(state.draftQuestions || {}),
      [questionId]: updatedQuestion,
    };

    return {
      ...state,
      draftQuestions,
      // Also mark as having unsaved changes
      hasUnsavedQuestionOrSectionChanges: true,
    };
  },
};

export const handleUpdateDraftQuestionUseCase: UseCaseStateGenerator<UpdateDraftQuestionUseCaseState> =
  {
    name: UPDATE_DRAFT_QUESTION_USE_CASE,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      const { questionId, updatedQuestion } = action.payload as {
        questionId: string;
        updatedQuestion: Question<QuestionProps>;
      };

      if (!state.draftQuestions || !state.draftQuestions[questionId]) {
        return state; // Draft question doesn't exist
      }

      // IMPORTANT: Clone the question to create a new reference
      // This ensures React detects the change and triggers re-renders in components
      // that use useQuestionContext() (like fill-in-blank, matching, etc.)
      // Single/multiple choice work because they also depend on question.answers reference
      const clonedQuestion = updatedQuestion.clone();

      const draftQuestions = {
        ...state.draftQuestions,
        [questionId]: clonedQuestion, // Use cloned question to ensure new reference
      };

      return {
        ...state,
        draftQuestions,
        hasUnsavedQuestionOrSectionChanges: true,
      };
    },
  };

export const handleCancelQuestionStateUseCase: UseCaseStateGenerator<UpdateQuestionUseCaseState> = {
  name: CANCEL_QUESTION_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      cancelQuestionStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const output = payload as CancelQuestionUseCaseOutput;
    const { canceledQuestion } = output;
    const questionId = canceledQuestion.id.toString();

    const sectionSummaries = [...state.sectionSummaries];
    const updatedSectionIndex = sectionSummaries.findIndex(
      (section) => section.id.toString() === canceledQuestion.sectionId,
    );

    // NOTE: Remove draft question from draftQuestions when user clicks cancel
    // This ensures draft changes are discarded and questions state is restored from backup
    const draftQuestions = { ...(state.draftQuestions || {}) };
    delete draftQuestions[questionId];

    if (canceledQuestion.status === QuestionStatus.Deactivate) {
      sectionSummaries[updatedSectionIndex] = SectionSummary.cloneFromSectionAndQuestions(
        sectionSummaries[updatedSectionIndex],
        state.backupQuestions,
      );

      return {
        ...state,
        sectionSummaries,
        questions: state.backupQuestions,
        ...getFormTotals(state.backupQuestions),
        headings: state.backupHeadings,
        cancelQuestionStatus: RequestStatus.SUCCESS,
        updateQuestionOrAnswerStatus: RequestStatus.RESET,
        hasUnsavedQuestionOrSectionChanges: false,
        editingQuestionId: null,
        revertedQuestion: null,
        isQuestionEditing: false,
        unsavedQuestion: null,
        selectedFormItemId: state.backupSelectedItem,
        editingFormItemId: '',
        draftQuestions, // Remove canceled question from drafts
      };
    }

    sectionSummaries[updatedSectionIndex] = SectionSummary.cloneFromSectionAndQuestions(
      sectionSummaries[updatedSectionIndex],
      state.backupQuestions,
    );

    return {
      ...state,
      questions: state.backupQuestions,
      sectionSummaries,
      ...getFormTotals(state.backupQuestions),
      cancelQuestionStatus: RequestStatus.SUCCESS,
      updateQuestionOrAnswerStatus: RequestStatus.RESET,
      hasUnsavedQuestionOrSectionChanges: false,
      editingQuestionId: null,
      revertedQuestion: null,
      isQuestionEditing: false,
      draftQuestions, // Remove canceled question from drafts
    };
  },
};

export const handleDeleteQuestionUseCase: UseCaseStateGenerator<DeleteQuestionUseCaseState> = {
  name: DELETE_QUESTION_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const { currentQuestion, isSkipApiCall } = payload as DeleteQuestionUseCaseInput;

    if (isSkipApiCall) {
      return {
        ...state,
        deletedQuestionId: payload,
        deleteQuestionStatus: RequestStatus.EXECUTE,
      };
    }

    const sectionSummaries = [...state.sectionSummaries];
    const sectionIdContainsDeletedQuestion = sectionSummaries
      .find((s) => s.id.toString() === currentQuestion.sectionId)
      ?.id.toString();

    return {
      ...state,
      deletedQuestionId: payload,
      sectionIdContainsDeletedQuestion,
      deleteQuestionStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const { deletedQuestion, newHeadings, newQuestions, formVersion } =
      payload as DeleteQuestionUseCaseOutput;
    const reSelectPreviousQuestionId =
      deletedQuestion.previousQuestionId === PreviousQuestionId.none.id.toString()
        ? deletedQuestion.sectionId || ''
        : deletedQuestion.previousQuestionId || '';

    const sortQuestions = Questions.sortQuestionsWithIndexAndSection(newQuestions);

    const sectionSummaries = [...state.sectionSummaries];
    const updatedSectionIndex = sectionSummaries.findIndex(
      (section) => section.id.toString() === deletedQuestion.sectionId,
    );

    // Check if section index is valid before updating to prevent errors
    if (updatedSectionIndex !== -1) {
      sectionSummaries[updatedSectionIndex] = SectionSummary.cloneFromSectionAndQuestions(
        sectionSummaries[updatedSectionIndex],
        sortQuestions,
      );
    }

    return {
      ...state,
      sectionSummaries,
      hasEssayOrFillInBlankQuestion: Questions.hasEssayOrFillInBlankQuestion(sortQuestions),
      editingQuestionId: '',
      hasUnsavedQuestionOrSectionChanges: false,
      questions: sortQuestions,
      isFormHasQuestions:
        (state.sections || []).length > 0 &&
        sortQuestions.some((q) => {
          const status = (q as any).status;
          return (
            status && status !== 'New' && status !== 'ChangeNewType' && status !== 'Deactivate'
          );
        }),
      ...getFormTotals(sortQuestions),
      deleteQuestionStatus: RequestStatus.SUCCESS,
      selectedFormItemId: reSelectPreviousQuestionId,
      editingFormItemId: '',
      headings: newHeadings,
      ...withSyncedFormVersion(state, formVersion),
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      deleteQuestionStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      deleteQuestionStatus: RequestStatus.RESET,
    };
  },
};

export const handleMoveQuestionUseCase: UseCaseStateGenerator<MoveQuestionUseCaseState> = {
  name: MOVE_QUESTION_USE_CASE,
  executing: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      moveQuestionStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { payload } = action;
    const { questions, headings, movedQuestion, formVersion } =
      payload as MoveQuestionUseCaseOutput;
    const sortQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

    const sectionSummaries = [...(state.sectionSummaries || [])];
    // Refresh all summaries so a source section that became empty still updates.
    const sectionIds = new Set((state.sectionSummaries || []).map((s) => s.id.toString()));
    sortQuestions.forEach((q) => sectionIds.add(q.sectionId));
    if (movedQuestion?.sectionId) {
      sectionIds.add(movedQuestion.sectionId);
    }

    sectionIds.forEach((sectionId) => {
      const updatedSectionIndex = sectionSummaries.findIndex(
        (section) => section.id.toString() === sectionId,
      );
      if (updatedSectionIndex !== -1) {
        sectionSummaries[updatedSectionIndex] = SectionSummary.cloneFromSectionAndQuestions(
          sectionSummaries[updatedSectionIndex],
          sortQuestions,
        );
      }
    });

    return {
      ...state,
      questions: sortQuestions,
      headings: headings ?? state.headings,
      sectionSummaries,
      hasEssayOrFillInBlankQuestion: Questions.hasEssayOrFillInBlankQuestion(sortQuestions),
      ...getFormTotals(sortQuestions),
      moveQuestionStatus: RequestStatus.SUCCESS,
      ...withSyncedFormVersion(state, formVersion),
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    const { error } = action;
    return {
      ...state,
      error,
      moveQuestionStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => {
    return {
      ...state,
      moveQuestionStatus: RequestStatus.RESET,
    };
  },
};

export const handleSetQuestionInViewUseCase: UseCaseStateGenerator<QuestionInViewUseCaseState> = {
  name: SET_QUESTION_IN_VIEW,
  executing: (state: FormEditPageState, action: Action) => {
    const questionId = action.payload;
    return {
      ...state,
      questionInView: questionId?.toString() || '',
    };
  },
  reset: (state: FormEditPageState) => {
    return {
      ...state,
      questionInView: null,
    };
  },
};
export const handleUpdateQuestionAudioFileUseCase: UseCaseStateGenerator<UpdateQuestionAudioFileUseCaseState> =
  {
    name: UPDATE_QUESTION_AUDIO_FILE_USE_CASE,
    executing: (state: FormEditPageState) => {
      return {
        ...state,
        updateQuestionAudioFileStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: FormEditPageState, action: Action) => {
      const { payload } = action;
      const updatedQuestion = payload as Question<QuestionProps>;
      const questionId = updatedQuestion.id.toString();

      // Update questions state
      const questions = state.questions.filter((q) => q.id.toString() !== questionId);

      questions.push(updatedQuestion);
      const sortQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

      // Update draft question if exists
      const draftQuestions = { ...(state.draftQuestions || {}) };
      if (draftQuestions[questionId]) {
        // Clone to ensure new reference for React re-render
        draftQuestions[questionId] = updatedQuestion.clone();
      }

      return {
        ...state,
        questions: sortQuestions,
        ...getFormTotals(sortQuestions),
        draftQuestions,
        updateQuestionAudioFileStatus: RequestStatus.SUCCESS,
        updateQuestionOrAnswerStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: FormEditPageState, action: Action) => {
      const { error } = action;
      return {
        ...state,
        error,
        updateQuestionAudioFileStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        updateQuestionAudioFileStatus: RequestStatus.RESET,
      };
    },
  };
export const handleDeleteQuestionAudioFileUseCase: UseCaseStateGenerator<DeleteQuestionAudioFileUseCaseState> =
  {
    name: DELETE_QUESTION_AUDIO_FILE_USE_CASE,
    executing: (state: FormEditPageState) => {
      return {
        ...state,
        deleteQuestionAudioFileStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: FormEditPageState, action: Action) => {
      const { payload } = action;
      const updatedQuestion = payload as Question<QuestionProps>;
      const questionId = updatedQuestion.id.toString();

      // Update questions state
      const questions = state.questions.filter((q) => q.id.toString() !== questionId);
      questions.push(updatedQuestion);
      const sortQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

      // Update draft question if exists
      const draftQuestions = { ...(state.draftQuestions || {}) };
      if (draftQuestions[questionId]) {
        // Clone to ensure new reference for React re-render
        draftQuestions[questionId] = updatedQuestion.clone();
      }

      return {
        ...state,
        questions: sortQuestions,
        ...getFormTotals(sortQuestions),
        draftQuestions,
        deleteQuestionAudioFileStatus: RequestStatus.SUCCESS,
        updateQuestionOrAnswerStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: FormEditPageState, action: Action) => {
      const { error } = action;
      return {
        ...state,
        error,
        deleteQuestionAudioFileStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        deleteQuestionAudioFileStatus: RequestStatus.RESET,
      };
    },
  };
export const handleAddQuestionAudioFileUseCase: UseCaseStateGenerator<AddQuestionAudioFileUseCaseState> =
  {
    name: ADD_QUESTION_AUDIO_FILE_USE_CASE,
    executing: (state: FormEditPageState) => {
      return {
        ...state,
        addQuestionAudioFileStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: FormEditPageState, action: Action) => {
      const { payload } = action;
      const updatedQuestion = payload as Question<QuestionProps>;
      const questionId = updatedQuestion.id.toString();

      // Update questions state
      const questions = state.questions.filter((q) => q.id.toString() !== questionId);
      questions.push(updatedQuestion);
      const sortQuestions = Questions.sortQuestionsWithIndexAndSection(questions);

      // Update draft question if exists
      const draftQuestions = { ...(state.draftQuestions || {}) };
      if (draftQuestions[questionId]) {
        // Clone to ensure new reference for React re-render
        draftQuestions[questionId] = updatedQuestion.clone();
      }

      return {
        ...state,
        questions: sortQuestions,
        ...getFormTotals(sortQuestions),
        draftQuestions,
        addQuestionAudioFileStatus: RequestStatus.SUCCESS,
        updateQuestionOrAnswerStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: FormEditPageState, action: Action) => {
      const { error } = action;
      return {
        ...state,
        error,
        addQuestionAudioFileStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => {
      return {
        ...state,
        addQuestionAudioFileStatus: RequestStatus.RESET,
      };
    },
  };

export const questionItemHandlers = [
  handleOpenEditQuestion,
  handleCreateLocalQuestionUseCase,
  handleDeleteQuestionUseCase,
  handleMoveQuestionUseCase,
  handleSaveQuestionUseCase,
  handleCreateQuestionFileUseCase,
  handleCreateQuestionAnswerFileUseCase,
  handleAddQuestionFileUseCase,
  handleUpdateQuestionTypeStateUseCase,
  handleUpdateQuestionStateUseCase,
  handleUpdateDraftQuestionUseCase,
  handleCancelQuestionStateUseCase,
  handleCreateAnswerStateUseCase,
  handleSetQuestionInViewUseCase,
  handleUpdateQuestionAudioFileUseCase,
  handleDeleteQuestionAudioFileUseCase,
  handleClearDraftQuestions,
  // answer handlers
  ...answerItemHandlers,
];
